import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react'
import '@/styles/liff-login.css'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QrCodeIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useAccount } from '@/hooks/useAccount'
import { sendOtp } from '@/lib/api'
import { loginThemeVars } from '@/lib/loginTheme'
import { useFitOneLine } from '@/hooks/useFitOneLine'

const LEASE_CODE_PATTERN = /^[A-Z0-9]{8}$/

export default function LeaseCodePage() {
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const { account } = useAccount()
  const navigate = useNavigate()
  const [leaseCode, setLeaseCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showScanner, setShowScanner] = useState(false)

  // Ref to track the Html5Qrcode instance so we can stop it on close
  const scannerRef = useRef<import('html5-qrcode').Html5Qrcode | null>(null)
  const titleRef = useFitOneLine(t('lease_code_title'))
  const scannerContainerId = 'qr-scanner-container'

  const submitLeaseCode = useCallback(
    async (code: string) => {
      const normalized = code.trim().toUpperCase()
      if (!normalized) return

      setIsLoading(true)
      setError(null)

      try {
        const result = await sendOtp(normalized)
        navigate('/otp', { state: { leaseCode: normalized, phoneHint: result.phone_hint } })
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 404) {
          setError(t('lease_not_found'))
        } else if (status === 429) {
          setError(t('rate_limited'))
        } else if (status === 422) {
          setError(t('no_phone'))
        } else if (status === 403) {
          // EP-150: the property's account is suspended or was deleted by
          // its owner. The tenant is told who to ask, never which of the two.
          setError(t('account_unavailable'))
        } else {
          setError(t('errors.something_went_wrong', { ns: 'common' }))
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate, t],
  )

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await submitLeaseCode(leaseCode)
  }

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop()
        scannerRef.current.clear()
      } catch {
        // Ignore errors when stopping — scanner may already be stopped
      }
      scannerRef.current = null
    }
  }, [])

  const closeScanner = useCallback(async () => {
    await stopScanner()
    setShowScanner(false)
  }, [stopScanner])

  // Start the camera scanner once the modal is visible and container is in DOM
  useEffect(() => {
    if (!showScanner) return

    let cancelled = false

    const startScanner = async () => {
      // Dynamic import keeps html5-qrcode out of the initial bundle
      const { Html5Qrcode } = await import('html5-qrcode')

      if (cancelled) return

      const scanner = new Html5Qrcode(scannerContainerId)
      scannerRef.current = scanner

      const onScanSuccess = async (decodedText: string) => {
        const value = decodedText.trim().toUpperCase()
        if (LEASE_CODE_PATTERN.test(value)) {
          await stopScanner()
          if (!cancelled) {
            setShowScanner(false)
            setLeaseCode(value)
            await submitLeaseCode(value)
          }
        } else {
          setError(t('scan_invalid_format'))
          await stopScanner()
          if (!cancelled) setShowScanner(false)
        }
      }

      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          onScanSuccess,
          // Ignore per-frame decode errors — they are normal while scanning
          undefined,
        )
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err)
          if (
            message.toLowerCase().includes('permission') ||
            message.toLowerCase().includes('denied')
          ) {
            setError(t('scan_permission_denied'))
          } else {
            setError(t('scan_permission_denied'))
          }
          setShowScanner(false)
        }
      }
    }

    void startScanner()

    return () => {
      cancelled = true
      void stopScanner()
    }
  }, [showScanner, stopScanner, submitLeaseCode, t])

  const theme = account?.liff_theme
  const panelEnabled = theme?.login_panel_enabled === 'true'
  const cssVars = loginThemeVars(theme)

  return (
    <div className="liff-login" style={cssVars}>
      <div className="liff-login__form-wrapper">
        {panelEnabled && <div className="liff-login__panel" />}
        <div className="liff-login__content">
          <h1 ref={titleRef} className="liff-login__title text-base-content">{t('lease_code_title')}</h1>

          <form onSubmit={(e) => { void handleSubmit(e) }} className="liff-login__fields">
            {/* No visible label (owner, 2026-10-01): the title already says what
                to enter. The label stays as the field's accessible name. The scan
                button sits on the field's row, icon only, its text kept as its
                accessible name (owner, same day). */}
            <div className="form-control">
              <div className="flex gap-2">
                <input
                  type="text"
                  aria-label={t('lease_code_label')}
                  className="input input-bordered flex-1 min-w-0 uppercase tracking-widest text-lg"
                  placeholder={t('lease_code_placeholder')}
                  value={leaseCode}
                  onChange={(e) => setLeaseCode(e.target.value.toUpperCase())}
                  maxLength={20}
                  autoComplete="off"
                  autoCapitalize="characters"
                />
                <button
                  type="button"
                  className="btn btn-outline btn-square shrink-0"
                  aria-label={t('scan_qr_code')}
                  title={t('scan_qr_code')}
                  disabled={isLoading}
                  onClick={() => {
                    setError(null)
                    setShowScanner(true)
                  }}
                >
                  <QrCodeIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            {error && (
              <div className="alert alert-error text-sm">
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="btn w-full liff-login__btn-primary"
              disabled={isLoading || !leaseCode.trim()}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                t('send_otp')
              )}
            </button>
          </form>
        </div>
      </div>

      {/* QR scanner modal overlay */}
      {showScanner && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80">
          <div className="relative w-full max-w-sm px-4">
            {/* Close button */}
            <button
              type="button"
              className="absolute -top-12 right-4 text-white p-2"
              onClick={() => { void closeScanner() }}
              aria-label="Cancel scanner"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>

            {/* Camera viewfinder container — html5-qrcode mounts its video here */}
            <div
              id={scannerContainerId}
              className="w-full rounded-xl overflow-hidden bg-black"
            />
          </div>

          <button
            type="button"
            className="mt-6 btn btn-ghost text-white"
            onClick={() => { void closeScanner() }}
          >
            {tc('actions.cancel')}
          </button>
        </div>
      )}
    </div>
  )
}
